'use client'

import { useState } from 'react'
import { useRouter, useParams } from 'next/navigation'
import Link from 'next/link'
import Image from 'next/image'
import { useLanguage } from '../lib/LanguageContext'
import { createClient } from '@/utils/supabase/client'
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Loader2, Eye, EyeOff } from 'lucide-react'
import { GoogleLogo } from "@/components/ui/google-logo"
import { ModeToggle } from '@/components/mode-toggle'
import { LanguageSwitcher } from '@/components/dashboard/LanguageSwitcher'
import { getAuthUrl, getMarketingUrl } from '@/lib/auth-urls'
import { StripedPattern } from '@/components/ui/striped-pattern'

export function LoginPage() {
    const [showPassword, setShowPassword] = useState(false)
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [isPending, startTransition] = useState(false)
    const [isGoogleLoading, setIsGoogleLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const router = useRouter()
    const params = useParams()
    const locale = (params.locale as string) || 'ar'
    const { dir } = useLanguage()

    // Simplified Translations
    const t = {
        back: locale === 'ar' ? 'العودة' : 'Back',
        title: locale === 'ar' ? 'مرحباً بعودتك' : 'Welcome back',
        subtitle: locale === 'ar' ? 'أدخل بريدك الإلكتروني للدخول إلى حسابك' : 'Enter your email below to login to your account',
        email: locale === 'ar' ? 'البريد الإلكتروني' : 'Email',
        password: locale === 'ar' ? 'كلمة المرور' : 'Password',
        forgot: locale === 'ar' ? 'نسيت كلمة المرور؟' : 'Forgot your password?',
        login: locale === 'ar' ? 'تسجيل الدخول' : 'Sign In',
        google: locale === 'ar' ? 'المتابعة باستخدام Google' : 'Continue with Google',
        or: locale === 'ar' ? 'أو' : 'OR',
        noAccount: locale === 'ar' ? 'ليس لديك حساب؟' : "Don't have an account?",
        signup: locale === 'ar' ? 'سجل الآن' : 'Sign up',
        loggingIn: locale === 'ar' ? 'جاري الدخول...' : 'Logging in...',
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setError(null)
        startTransition(true)

        try {
            const res = await fetch('/api/auth/login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password }),
            })

            const data = await res.json()

            if (!res.ok) {
                setError(data.error || 'Login failed')
                startTransition(false)
                return
            }

            router.push(`/${locale}/dashboard`)
            router.refresh()
        } catch (err) {
            console.error(err)
            setError('An unexpected error occurred')
            startTransition(false)
        }
    }

    const handleGoogleLogin = async () => {
        setIsGoogleLoading(true)
        setError(null)
        const supabase = createClient()
        const { error } = await supabase.auth.signInWithOAuth({
            provider: 'google',
            options: {
                redirectTo: `${window.location.origin}/auth/callback?redirect=/${locale}/dashboard`,
                queryParams: { access_type: 'offline' },
            },
        })
        if (error) {
            setError(error.message)
            setIsGoogleLoading(false)
        }
    }

    return (
        <div className="h-screen flex items-center justify-center bg-background relative overflow-hidden" dir={dir}>
            {/* Global Background Pattern - Moved to lowest Z-index */}
            <StripedPattern
                className="text-foreground/[0.03] dark:text-foreground/[0.02] z-0"
            />

            {/* Controls - Fixed to top start corner of the screen/form side */}
            <div className="absolute top-4 left-4 rtl:left-auto rtl:right-4 z-50 flex items-center gap-2">
                <LanguageSwitcher locale={locale} />
                <ModeToggle />
            </div>

            <div className="w-full h-full grid lg:grid-cols-2 p-4 relative z-10">
                {/* Form Side */}
                <div className="flex h-full flex-col items-center justify-center">
                    <div className="max-w-xs w-full flex flex-col items-center">
                        <Link href={getMarketingUrl(locale)} className="mb-6 flex flex-col items-center gap-2 font-bold hover:opacity-90 transition-opacity">
                            {/* Light Mode Logo (Black) */}
                            <Image
                                src="/logo-light.png"
                                alt="Ecommaps Logo"
                                width={40}
                                height={40}
                                className="size-10 object-contain block dark:hidden"
                                unoptimized
                            />
                            {/* Dark Mode Logo (White) */}
                            <Image
                                src="/logo-dark.png"
                                alt="Ecommaps Logo"
                                width={40}
                                height={40}
                                className="size-10 object-contain hidden dark:block"
                                unoptimized
                            />
                        </Link>

                        <h1 className="text-xl font-semibold tracking-tight">{t.title}</h1>
                        <p className="mt-2 text-center text-sm text-balance text-muted-foreground">{t.subtitle}</p>

                        <div className="w-full mt-8 grid gap-6">
                            {error && (
                                <div className="bg-destructive/15 text-destructive text-sm p-3 rounded-md text-center">
                                    {error}
                                </div>
                            )}

                            <Button
                                variant="outline"
                                type="button"
                                className="w-full gap-3 bg-background hover:bg-muted/50 dark:ring-input border-input/50 dark:border-input border-b-2 shadow-sm shadow-zinc-950/15 ring-1 ring-zinc-300 rounded-md"
                                disabled={isPending || isGoogleLoading}
                                onClick={handleGoogleLogin}
                            >
                                {isGoogleLoading ? (
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                ) : (
                                    <GoogleLogo />
                                )}
                                {t.google}
                            </Button>

                            <div className="relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t after:border-border">
                                <span className="relative z-10 bg-background px-2 text-muted-foreground">
                                    {t.or}
                                </span>
                            </div>

                            <form onSubmit={handleSubmit} className="w-full space-y-4">
                                <div className="grid gap-2">
                                    <Label htmlFor="email">{t.email}</Label>
                                    <Input
                                        id="email"
                                        placeholder="name@example.com"
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        disabled={isPending || isGoogleLoading}
                                        required
                                        className="bg-background/80 backdrop-blur-sm"
                                    />
                                </div>

                                <div className="grid gap-2">
                                    <Label htmlFor="password">{t.password}</Label>
                                    <div className="relative">
                                        <Input
                                            id="password"
                                            type={showPassword ? "text" : "password"}
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            disabled={isPending || isGoogleLoading}
                                            required
                                            className="bg-background/80 backdrop-blur-sm"
                                        />
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent ltr:right-0 rtl:left-0 rtl:right-auto"
                                            onClick={() => setShowPassword(!showPassword)}
                                        >
                                            {showPassword ? (
                                                <EyeOff className="h-4 w-4 text-muted-foreground" />
                                            ) : (
                                                <Eye className="h-4 w-4 text-muted-foreground" />
                                            )}
                                        </Button>
                                    </div>
                                </div>

                                <Button
                                    type="submit"
                                    className="w-full mt-4 from-primary to-primary/85 text-primary-foreground dark:inset-shadow-2xs dark:inset-shadow-white/10 bg-linear-to-t border border-b-2 border-zinc-950/40 shadow-md shadow-zinc-950/20 ring-1 ring-inset ring-white/25 transition-[filter] duration-200 hover:brightness-110 active:brightness-90 dark:border-x-0 dark:border-t-0 dark:border-zinc-950/50 dark:ring-white/5 rounded-md"
                                    disabled={isPending || isGoogleLoading}
                                >
                                    {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                    {isPending ? t.loggingIn : t.login}
                                </Button>
                            </form>

                            <div className="mt-5 space-y-5 text-center">
                                <Link
                                    href={`/${locale}/forgot-password`}
                                    className="text-sm block underline text-muted-foreground hover:text-primary transition-colors"
                                >
                                    {t.forgot}
                                </Link>
                                <p className="text-sm">
                                    {t.noAccount}{" "}
                                    <Link href={getAuthUrl(locale, '/signup')} className="ml-1 underline text-muted-foreground hover:text-primary transition-colors">
                                        {t.signup}
                                    </Link>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Image Side */}
                <div className="hidden bg-muted lg:block rounded-lg border relative overflow-hidden bg-slate-900 shadow-sm">
                    <div className="absolute inset-0 bg-[url('https://ecodata.ecommaps.com/storage/v1/object/public/store-assets/login-background-final.png')] bg-cover bg-center"></div>
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/40 to-transparent"></div>
                    <div className="relative z-20 flex h-full flex-col justify-between p-12 text-white pointer-events-none">
                        <div className="flex items-center gap-2 text-2xl font-bold">
                            <Image
                                src="/logo-dark.png"
                                alt="Ecommaps Logo"
                                width={32}
                                height={32}
                                className="w-8 h-8 object-contain"
                                unoptimized
                            />
                            Ecommaps
                        </div>
                        <div className="space-y-4">
                            <blockquote className="space-y-2">
                                <p className="text-lg">
                                    &ldquo;This platform has completely transformed how I manage my business. The speed and analytics are unmatched.&rdquo;
                                </p>
                                <footer className="text-sm text-slate-300">Sofia Davis, CEO of TechStart</footer>
                            </blockquote>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
